import { Check, Clock, Mic, UserMinus, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { ProfileOrganization } from "@/components/app/profile-organization";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isUserModerator } from "@/hooks/use-stage";
import { useUser } from "@/hooks/use-user";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

type StageRequestQueueProps = {
	channelId: string;
};

export function StageRequestQueue({ channelId }: StageRequestQueueProps) {
	const stageStates = useStore(coreStore, (state) => state.stageStates);
	const users = useStore(coreStore, (state) => state.users);
	const roles = useStore(coreStore, (state) => state.roles);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const approveStageRequest = useStore(
		coreStore,
		(state) => state.approveStageRequest
	);
	const denyStageRequest = useStore(
		coreStore,
		(state) => state.denyStageRequest
	);
	const removeFromStage = useStore(coreStore, (state) => state.removeFromStage);
	const { getUserDisplayName } = useUser();

	const stageState = stageStates.get(channelId);
	const requests = stageState?.requests ?? [];
	const speakers = stageState?.speakers ?? [];

	const handleApprove = async (odId: string) => {
		try {
			await approveStageRequest(channelId, odId);
			toast.success("Request approved");
		} catch {
			toast.error("Failed to approve request");
		}
	};

	const handleDeny = async (odId: string) => {
		try {
			await denyStageRequest(channelId, odId);
			toast.info("Request denied");
		} catch {
			toast.error("Failed to deny request");
		}
	};

	const handleKick = async (odId: string) => {
		try {
			await removeFromStage(channelId, odId);
			toast.info("User removed from stage");
		} catch {
			toast.error("Failed to remove user from stage");
		}
	};

	const formatTime = (isoString: string) => {
		const MS_PER_MINUTE = 60_000;
		const date = new Date(isoString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / MS_PER_MINUTE);

		if (diffMins < 1) return "Just now";
		if (diffMins === 1) return "1 min ago";
		if (diffMins < 60) return `${diffMins} mins ago`;

		const diffHours = Math.floor(diffMins / 60);
		if (diffHours === 1) return "1 hour ago";
		return `${diffHours} hours ago`;
	};

	const getInitials = (name: string) =>
		name
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button className="relative" size="sm" variant="secondary">
					<Mic className="size-4" />
					{requests.length > 0 && (
						<span className="-top-1 -right-1 absolute flex size-5 items-center justify-center rounded-full bg-primary font-medium text-[10px] text-primary-foreground">
							{requests.length}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80 p-0">
				<div className="border-b px-4 py-3">
					<h4 className="font-semibold text-sm">Stage Management</h4>
					<p className="text-muted-foreground text-xs">
						{speakers.length} on stage · {requests.length} pending
					</p>
				</div>
				<ScrollArea className="max-h-80">
					{speakers.length > 0 && (
						<div className="border-b p-2">
							<p className="mb-2 px-2 font-medium text-muted-foreground text-xs">
								On Stage
							</p>
							<div className="space-y-1">
								{speakers.map((odId) => {
									const displayName = getUserDisplayName(odId);
									const userProfile = users.get(odId)?.profile;
									const userRoles = users.get(odId)?.roles ?? [];
									const avatar = userProfile?.profilePhoto ?? undefined;
									const isModerator = isUserModerator(
										userRoles,
										roles,
										currentWorkspaceId
									);

									return (
										<div
											className="flex items-center gap-3 rounded-lg p-2"
											key={odId}
										>
											<Avatar className="size-8">
												<AvatarImage alt={displayName} src={avatar} />
												<AvatarFallback className="text-xs">
													{getInitials(displayName)}
												</AvatarFallback>
											</Avatar>
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium text-sm">
													{displayName}
												</p>
												<ProfileOrganization className="block" uid={odId} />
											</div>
											{!isModerator && (
												<Button
													className="size-7 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20"
													onClick={() => handleKick(odId)}
													size="icon"
													title="Remove from stage"
													variant="ghost"
												>
													<UserMinus className="size-3.5" />
													<span className="sr-only">Remove from stage</span>
												</Button>
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{requests.length > 0 && (
						<div className="p-2">
							<p className="mb-2 px-2 font-medium text-muted-foreground text-xs">
								Requests
							</p>
							<div className="space-y-1">
								{requests.map((request) => {
									const displayName = getUserDisplayName(request.odId);
									const userProfile = users.get(request.odId)?.profile;
									const avatar = userProfile?.profilePhoto ?? undefined;

									return (
										<div
											className="flex items-center gap-3 rounded-lg p-2"
											key={request.odId}
										>
											<Avatar className="size-8">
												<AvatarImage alt={displayName} src={avatar} />
												<AvatarFallback className="text-xs">
													{getInitials(displayName)}
												</AvatarFallback>
											</Avatar>
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium text-sm">
													{displayName}
												</p>
												<ProfileOrganization
													className="block"
													uid={request.odId}
												/>
												<p className="text-muted-foreground text-xs">
													{formatTime(request.requestedAt)}
												</p>
											</div>
											<div className="flex gap-1">
												<Button
													className="size-7 rounded-full bg-green-500/10 text-green-600 hover:bg-green-500/20"
													onClick={() => handleApprove(request.odId)}
													size="icon"
													variant="ghost"
												>
													<Check className="size-3.5" />
													<span className="sr-only">Approve</span>
												</Button>
												<Button
													className="size-7 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20"
													onClick={() => handleDeny(request.odId)}
													size="icon"
													variant="ghost"
												>
													<X className="size-3.5" />
													<span className="sr-only">Deny</span>
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{speakers.length === 0 && requests.length === 0 && (
						<div className="flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
							<Clock className="mb-2 size-8 opacity-50" />
							<p className="text-xs">No one on stage yet</p>
						</div>
					)}
				</ScrollArea>
			</PopoverContent>
		</Popover>
	);
}
